import { useEffect, useState } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { useApp } from "../state/store";
import { Button, DetailsPanel, Field, ScreenHeading, StatusLine } from "../ui";
import { Screen } from "./screen";
import { useScreenStatus } from "./use-screen-status";

/** Where the emailed link puts its token: the fragment, which no server or scanner sees. */
const TOKEN_MARKER = "#delete=";

/** The token in this page's URL, if it was opened from the emailed link. */
export function deleteTokenFromHash(hash: string): string | null {
  if (!hash.startsWith(TOKEN_MARKER)) return null;
  const token = decodeURIComponent(hash.slice(TOKEN_MARKER.length)).trim();
  return token.length >= 16 ? token : null;
}

/** Where each store's own subscriptions page is: the only place its plan can be cancelled. */
export const STORE_SUBSCRIPTIONS_URL = {
  apple_iap: "https://apps.apple.com/account/subscriptions",
  google_play: "https://play.google.com/store/account/subscriptions",
} as const;

export type StorePlan = keyof typeof STORE_SUBSCRIPTIONS_URL;

/**
 * What deleting the account does to a plan (#887). `web`: a plan bought here, which the
 * deletion cancels. `store`: a plan from the App Store or Google Play that will charge again,
 * which only the person can cancel. `unknown` when nobody is signed in to ask (the emailed
 * link) or the wallet didn't load: a general line about store plans.
 */
export type PlanOnDelete = { web: boolean; store: StorePlan | null } | "unknown";

/**
 * What deleting an account does, in the order the person cares about: what goes, what the
 * others keep, the credits -- then a plan, and that it cannot be undone. One list for the
 * confirm step on Account and on this page, so the two can never promise different things.
 */
export function DeleteConsequences({ plan = "unknown" }: { plan?: PlanOnDelete }) {
  const store = plan === "unknown" ? null : plan.store;
  return (
    <>
      <p className="m-0 text-lede leading-normal">{t("account.delete_lede")}</p>
      <ul className="my-stack pl-pad list-disc grid gap-tight text-note leading-normal text-ink-soft">
        <li><strong>{t("account.delete_meetings")}</strong><p>{t("account.delete_solo")}</p><p>{t("account.delete_shared")}</p></li>
        <li><strong>{t("account.delete_balance")}</strong><p>{t("account.delete_credits")}</p></li>
        {plan !== "unknown" && plan.web ? <li>{t("account.delete_plan_web")}</li> : null}
      </ul>
      <DetailsPanel summary={t("account.what_kept")}><p>{t("account.kept_detail")}</p></DetailsPanel>
      {store ? (
        <p data-store-plan={store} className="m-0 mb-stack text-note leading-normal text-danger">
          {t(store === "apple_iap" ? "account.delete_plan_app_store" : "account.delete_plan_google_play")}{" "}
          <a href={STORE_SUBSCRIPTIONS_URL[store]} target="_blank" rel="noreferrer" className="underline"
            onClick={() => captureAnalytics("link_opened", { link: "store_subscriptions" })}
          >
            {t(
              store === "apple_iap"
                ? "account.delete_plan_open_app_store"
                : "account.delete_plan_open_google_play"
            )}
          </a>
        </p>
      ) : plan === "unknown" ? (
        <p className="m-0 mb-stack text-note leading-normal text-ink-soft">
          {t("delete_account.store_plan_note")}
        </p>
      ) : null}
      <p className="m-0 text-note font-semibold text-danger">{t("account.delete_cannot_undo")}</p>
    </>
  );
}

export type DeleteStage =
  | { kind: "form" }
  | { kind: "sent"; email: string }
  | { kind: "confirm"; token: string }
  | { kind: "expired" }
  | { kind: "done" };

/**
 * Delete an account without signing in (#781): the page the app stores link to.
 *
 * Two halves, both unguarded, because the person may have no app and no sign-in here:
 *
 * - **The form.** An email, then "check your inbox". The server answers the same whether or
 *   not the address has an account, so this page never says which.
 * - **The confirm step**, when the emailed link opens it (`#delete=<token>`). The token is
 *   read once and taken out of the address bar, then nothing happens until the person taps
 *   Delete account: some mail scanners run a page's script, and opening the link alone must
 *   never delete anybody's account.
 *
 * When this browser was signed in to the account just deleted, it is signed out here too:
 * the server has already ended that sign-in.
 */
export function DeleteAccount({ initialStage }: { initialStage?: DeleteStage } = {}) {
  // `initialStage` is the parity gallery's seed; the app always starts from the URL.
  const [stage, setStage] = useState<DeleteStage>(() => {
    if (initialStage) return initialStage;
    const token = deleteTokenFromHash(window.location.hash);
    return token ? { kind: "confirm", token } : { kind: "form" };
  });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, say] = useScreenStatus();

  // The token is spent from memory; leaving it in the URL would put it in history.
  useEffect(() => {
    if (window.location.hash.startsWith(TOKEN_MARKER)) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  async function send() {
    const address = email.trim();
    if (!address.includes("@")) {
      say(t("signin.error_email"), "err");
      return;
    }
    setBusy(true);
    try {
      await api.requestAccountDeletion(address);
      say("");
      setStage({ kind: "sent", email: address });
    } catch (err) {
      const status = (err as { status?: number }).status;
      // A refusal with a reason of the server's own (the limit's "check your inbox") says
      // more than our generic line; a failure to reach it does not.
      say(status === 429 ? (err as Error).message : t("delete_account.send_failed"), "err");
    } finally {
      setBusy(false);
    }
  }

  async function confirm(token: string) {
    captureAnalytics("account_delete_started", {});
    setBusy(true);
    try {
      await api.confirmAccountDeletion(token);
      captureAnalytics("account_deleted", {});
      say("");
      useApp.getState().setAccount(null);
      setStage({ kind: "done" });
    } catch (err) {
      if ((err as { status?: number }).status === 400) {
        say("");
        setStage({ kind: "expired" });
      } else {
        say(t("account.delete_failed"), "err");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen name="delete_account">
      {stage.kind === "confirm" ? (
        <>
          <ScreenHeading>{t("delete_account.confirm_heading")}</ScreenHeading>
          <DeleteConsequences />
          <Button
            danger
            className="w-full mt-pad"
            disabled={busy}
            onClick={() => void confirm(stage.token)}
          >
            {busy ? t("account.delete_working") : t("delete_account.confirm")}
          </Button>
        </>
      ) : stage.kind === "done" ? (
        <>
          <ScreenHeading>{t("delete_account.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal">{t("delete_account.done")}</p>
        </>
      ) : stage.kind === "expired" ? (
        <>
          <ScreenHeading>{t("delete_account.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal">{t("delete_account.link_expired")}</p>
          <Button className="w-full mt-pad" onClick={() => setStage({ kind: "form" })}>
            {t("delete_account.request_new")}
          </Button>
        </>
      ) : stage.kind === "sent" ? (
        <>
          <ScreenHeading>{t("delete_account.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal wrap-anywhere">
            {t("delete_account.sent", { email: stage.email })}
          </p>
        </>
      ) : (
        <>
          <ScreenHeading>{t("delete_account.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal">{t("delete_account.lede")}</p>
          <Field
            label={t("delete_account.email_label")}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={t("signin.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button className="w-full" disabled={busy || email.trim() === ""} onClick={() => void send()}>
            {busy ? t("delete_account.sending") : t("delete_account.send")}
          </Button>
        </>
      )}
      <StatusLine kind={status.kind}>{status.message}</StatusLine>
    </Screen>
  );
}
